/**
 * Multi-Agent Orchestrator with Count-Based Provisioning
 * 
 * Implements the 4-phase orchestration model for Microsoft Graph API operations:
 * 1. Intent Parsing with Count Recognition
 * 2. Agent Provisioning Matrix
 * 3. Structured Agent Configuration
 * 4. Parallel/Sequential Execution with Result Aggregation
 * 
 * Example:
 * "Find 3 emails by contact, 2 meetings by user, and search 3 topics"
 * → Creates 8 specialized agents (3 EmailSearch + 2 MeetingSearch + 3 TopicSearch)
 * → Executes in parallel waves
 * → Aggregates and synthesizes results
 */

import { blink } from '@/lib/blink';
import { gpt5NanoAgent, AgentResponse } from './gpt5NanoAgent';
import { agentCacheManager, AgentCacheManager } from './agentCacheManager';
import { circuitBreakerManager } from './agentCircuitBreaker';
import { retryStrategyManager } from './agentRetryStrategy';
import { safeJsonParse } from '@/lib/utils';

/**
 * Task breakdown with count-specific parameters
 */
export interface CountBasedTask {
  task_id: string;
  task_type: 'email_search' | 'calendar_search' | 'topic_search' | 'web_research' | 'contact_analysis';
  count: number;
  items: Array<{
    iteration: number;
    target: string;
    parameters: Record<string, any>;
  }>;
  api_calls_required: string[];
  execution_status: 'pending' | 'running' | 'completed' | 'failed';
  wave: number; // Execution wave for dependency management
}

/**
 * Agent instance with dedicated configuration
 */
export interface AgentInstance {
  agent_id: string;
  task_id: string;
  iteration: number;
  target: any;
  graph_api_call?: {
    endpoint: string;
    query: string;
  };
  web_api_call?: {
    endpoint: string;
    query: string;
  };
  prompt: string;
  structured_output_schema: Record<string, any>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: any;
  error?: string;
  duration_ms?: number;
}

/**
 * Orchestration plan with wave-based execution
 */
export interface OrchestrationPlan {
  primary_action: string;
  task_breakdown: CountBasedTask[];
  total_agents_needed: number;
  execution_waves: Array<{
    wave_id: number;
    agents: AgentInstance[];
    dependency: 'none' | 'previous_wave';
  }>;
  estimated_duration_ms: number;
}

/**
 * Final orchestration result with aggregation
 */
export interface OrchestrationResult {
  success: boolean;
  workflow_summary: {
    total_tasks: number;
    total_agents_deployed: number;
    execution_time_ms: number;
    success_rate: number;
  };
  results_by_category: Record<string, any>;
  cross_correlation_insights: string[];
  recommended_actions: string[];
  raw_agent_results: AgentInstance[];
}

/**
 * Phase 1: Intent Parser with Count Recognition
 */
export class IntentParser {
  /**
   * Parse user request to extract count-based tasks
   */
  async parseIntent(userRequest: string): Promise<{
    primary_action: string;
    tasks: Array<{
      type: string;
      count: number;
      targets: string[];
      filters?: Record<string, any>;
    }>;
  }> {
    console.log('🔍 Phase 1: Intent Parsing with Count Recognition');
    
    const { text } = await blink.ai.generateText({
      prompt: `You are a GPT-5 orchestrator parsing user requests for Microsoft Graph API operations.

User Request: "${userRequest}"

Extract and structure the request into count-based tasks. Identify:
1. How many items of each type (emails, meetings, contacts, topics)
2. What targets/filters for each item
3. Which Graph API operations are needed

Respond with JSON:
{
  "primary_action": "description",
  "tasks": [
    {
      "type": "email_search_by_contact",
      "count": 3,
      "targets": ["contact1@company.com", "contact2@company.com", "contact3@company.com"],
      "filters": {"maxResults": 50}
    },
    {
      "type": "calendar_search_by_timeframe",
      "count": 1,
      "targets": ["last_week"],
      "filters": {"includeAttendees": true}
    },
    {
      "type": "topic_search",
      "count": 3,
      "targets": ["project_alpha", "budget_review", "team_updates"]
    }
  ]
}

Extract all counts and targets from the user request. Be specific and structured.`,
      maxTokens: 1000
    });

    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Failed to parse intent - no JSON found');
    }

    return safeJsonParse(jsonMatch[0]);
  }
}

/**
 * Phase 2: Agent Provisioning Matrix
 */
export class AgentProvisioner {
  /**
   * Create dedicated micro-agents for each count-based task
   */
  provisionAgents(parsedIntent: any): OrchestrationPlan {
    console.log('🤖 Phase 2: Agent Provisioning Matrix');
    
    const taskBreakdown: CountBasedTask[] = [];
    const executionWaves: OrchestrationPlan['execution_waves'] = [];
    let totalAgents = 0;
    let currentWave = 0;

    // Group tasks by dependency
    const graphApiTasks = parsedIntent.tasks.filter((t: any) => 
      t.type.includes('email') || t.type.includes('calendar') || t.type.includes('contact')
    );
    const webResearchTasks = parsedIntent.tasks.filter((t: any) => 
      t.type.includes('web') || t.type.includes('research')
    );

    // Wave 1: Graph API operations (parallel)
    if (graphApiTasks.length > 0) {
      const wave1Agents: AgentInstance[] = [];
      
      graphApiTasks.forEach((task: any) => {
        const countBasedTask: CountBasedTask = {
          task_id: `${task.type}_${Date.now()}`,
          task_type: this.mapToTaskType(task.type),
          count: task.count,
          items: task.targets.map((target: string, idx: number) => ({
            iteration: idx + 1,
            target,
            parameters: task.filters || {}
          })),
          api_calls_required: this.getApiCalls(task.type),
          execution_status: 'pending',
          wave: currentWave
        };

        taskBreakdown.push(countBasedTask);

        // Create agent for each count
        task.targets.forEach((target: string, idx: number) => {
          const agent = this.createAgent(task.type, idx + 1, target, task.filters || {});
          wave1Agents.push(agent);
          totalAgents++;
        });
      });

      executionWaves.push({
        wave_id: currentWave,
        agents: wave1Agents,
        dependency: 'none'
      });
      currentWave++;
    }

    // Wave 2: Web research (parallel, depends on Wave 1)
    if (webResearchTasks.length > 0) {
      const wave2Agents: AgentInstance[] = [];
      
      webResearchTasks.forEach((task: any) => {
        const countBasedTask: CountBasedTask = {
          task_id: `${task.type}_${Date.now()}`,
          task_type: 'web_research' as any,
          count: task.count,
          items: task.targets.map((target: string, idx: number) => ({
            iteration: idx + 1,
            target,
            parameters: {}
          })),
          api_calls_required: ['web_search_api'],
          execution_status: 'pending',
          wave: currentWave
        };

        taskBreakdown.push(countBasedTask);

        task.targets.forEach((target: string, idx: number) => {
          const agent = this.createWebAgent(task.type, idx + 1, target);
          wave2Agents.push(agent);
          totalAgents++;
        });
      });

      executionWaves.push({
        wave_id: currentWave,
        agents: wave2Agents,
        dependency: 'previous_wave'
      });
    }

    return {
      primary_action: parsedIntent.primary_action,
      task_breakdown: taskBreakdown,
      total_agents_needed: totalAgents,
      execution_waves: executionWaves,
      estimated_duration_ms: totalAgents * 2000 // Estimate 2s per agent
    };
  }

  /**
   * Phase 3: Create structured agent configuration
   */
  private createAgent(
    taskType: string, 
    iteration: number, 
    target: string, 
    filters: Record<string, any>
  ): AgentInstance {
    const agentId = `${taskType.replace(/_/g, '')}Agent_${iteration}`;
    
    // Map task types to Graph API operations
    const apiConfig = this.getGraphApiConfig(taskType, target, filters);
    
    return {
      agent_id: agentId,
      task_id: taskType,
      iteration,
      target,
      graph_api_call: apiConfig,
      prompt: this.generateAgentPrompt(taskType, target, filters),
      structured_output_schema: this.getOutputSchema(taskType),
      status: 'pending'
    };
  }

  /**
   * Create web research agent
   */
  private createWebAgent(taskType: string, iteration: number, target: string): AgentInstance {
    const agentId = `WebResearchAgent_${iteration}`;
    
    return {
      agent_id: agentId,
      task_id: taskType,
      iteration,
      target,
      web_api_call: {
        endpoint: 'web_search_api',
        query: target
      },
      prompt: `Research ${target} using web search. Provide comprehensive information including recent news, key facts, and relevance.`,
      structured_output_schema: {
        overview: 'string',
        recent_news: ['object'],
        key_facts: ['string'],
        relevance_score: 'number'
      },
      status: 'pending'
    };
  }

  /**
   * Get Graph API configuration for task type
   */
  private getGraphApiConfig(
    taskType: string, 
    target: string, 
    filters: Record<string, any>
  ): { endpoint: string; query: string } {
    switch (taskType) {
      case 'email_search_by_contact':
        return {
          endpoint: 'GET /me/messages',
          query: `$filter=from/emailAddress/address eq '${target}'&$top=${filters.maxResults || 50}`
        };
      case 'calendar_search_by_user':
        return {
          endpoint: 'GET /me/calendar/events',
          query: `$filter=attendees/any(a:a/emailAddress/address eq '${target}')&$top=${filters.maxResults || 50}`
        };
      case 'calendar_search_by_topic':
        return {
          endpoint: 'GET /me/calendar/events',
          query: `$search="${target}"&$top=${filters.maxResults || 50}`
        };
      case 'topic_search':
        return {
          endpoint: 'GET /me/messages',
          query: `$search="${target}"&$top=${filters.maxResults || 50}`
        };
      default:
        return {
          endpoint: 'GET /me/messages',
          query: `$top=50`
        };
    }
  }

  /**
   * Generate structured prompt for agent
   */
  private generateAgentPrompt(taskType: string, target: string, filters: Record<string, any>): string {
    switch (taskType) {
      case 'email_search_by_contact':
        return `Search for all emails from ${target} in the user's mailbox. Return email subjects, dates, and preview text organized by importance. Analyze communication patterns and recent activity.`;
      case 'calendar_search_by_user':
        return `Find all meetings with ${target}. Return meeting subjects, times, and attendee counts. Analyze meeting frequency and collaboration patterns.`;
      case 'calendar_search_by_topic':
        return `Search for meetings related to "${target}". Return relevant meetings with context and significance. Identify key themes and decisions.`;
      case 'topic_search':
        return `Search emails for discussions about "${target}". Extract key conversations, decisions, and action items. Provide context and timeline.`;
      default:
        return `Process ${target} and provide structured analysis.`;
    }
  }

  /**
   * Get structured output schema for task type
   */
  private getOutputSchema(taskType: string): Record<string, any> {
    const commonEmailSchema = {
      emails_found: 'number',
      email_list: ['object'],
      communication_patterns: {
        frequency: 'string',
        recent_activity: 'datetime',
        key_topics: ['string']
      }
    };

    const commonMeetingSchema = {
      meetings_found: 'number',
      meeting_list: ['object'],
      collaboration_patterns: {
        frequency: 'string',
        common_meeting_types: ['string'],
        collaboration_score: 'number'
      }
    };

    switch (taskType) {
      case 'email_search_by_contact':
      case 'topic_search':
        return commonEmailSchema;
      case 'calendar_search_by_user':
      case 'calendar_search_by_topic':
        return commonMeetingSchema;
      default:
        return { result: 'object' };
    }
  }

  private mapToTaskType(type: string): CountBasedTask['task_type'] {
    if (type.includes('email')) return 'email_search';
    if (type.includes('calendar')) return 'calendar_search';
    if (type.includes('topic')) return 'topic_search';
    if (type.includes('contact')) return 'contact_analysis';
    return 'web_research' as any;
  }

  private getApiCalls(type: string): string[] {
    if (type.includes('email')) return ['GET /me/messages'];
    if (type.includes('calendar')) return ['GET /me/calendar/events', 'GET /me/calendar/calendarView'];
    if (type.includes('contact')) return ['GET /me/contacts'];
    return ['web_search'];
  }
}

/**
 * Phase 4: Execution Engine with Parallel/Sequential Control
 * Enhanced with caching, circuit breaker, and adaptive retry
 */
export class ExecutionEngine {
  private dynamicWaveSize = {
    initialSize: 5,
    maxSize: 20,
    minSize: 2,
    adjustmentFactor: 1.5
  };
  private performanceMetrics = {
    avgSuccessTime: 0,
    avgFailureTime: 0,
    totalExecutions: 0
  };

  /**
   * Execute orchestration plan with dynamic wave sizing and enhanced reliability
   */
  async execute(
    plan: OrchestrationPlan,
    onProgress?: (agent: AgentInstance) => void
  ): Promise<OrchestrationResult> {
    console.log('⚡ Phase 4: Parallel/Sequential Execution with Dynamic Wave Sizing');
    console.log(`   Total agents: ${plan.total_agents_needed}`);
    console.log(`   Execution waves: ${plan.execution_waves.length}`);
    
    const startTime = Date.now();
    const allResults: AgentInstance[] = [];
    let currentWaveSize = this.dynamicWaveSize.initialSize;

    // Execute waves with dynamic sizing
    for (const wave of plan.execution_waves) {
      console.log(`\n🌊 Executing Wave ${wave.wave_id} (${wave.agents.length} agents)`);
      
      // Split large waves into dynamic batches
      const batches = this.splitIntoBatches(wave.agents, currentWaveSize);
      console.log(`   Split into ${batches.length} batches of ~${currentWaveSize} agents each`);

      for (const batch of batches) {
        const batchStart = Date.now();
        
        const batchResults = await Promise.allSettled(
          batch.map(agent => this.executeAgent(agent, onProgress))
        );

        // Process batch results
        const successCount = batchResults.filter(r => r.status === 'fulfilled').length;
        const batchDuration = Date.now() - batchStart;

        batchResults.forEach((result, idx) => {
          const agent = batch[idx];
          if (result.status === 'fulfilled') {
            allResults.push(result.value);
          } else {
            agent.status = 'failed';
            agent.error = String(result.reason);
            allResults.push(agent);
          }
        });

        // Adjust wave size based on performance
        currentWaveSize = this.adjustWaveSize(
          currentWaveSize,
          successCount,
          batch.length,
          batchDuration
        );

        console.log(`   Batch completed: ${successCount}/${batch.length} succeeded, next size: ${currentWaveSize}`);
      }
    }

    // Update performance metrics
    this.updatePerformanceMetrics(allResults);

    // Aggregate and synthesize results
    return await this.aggregateResults(plan, allResults, Date.now() - startTime);
  }

  /**
   * Split agents into dynamic batches
   */
  private splitIntoBatches(agents: AgentInstance[], batchSize: number): AgentInstance[][] {
    const batches: AgentInstance[][] = [];
    for (let i = 0; i < agents.length; i += batchSize) {
      batches.push(agents.slice(i, i + batchSize));
    }
    return batches;
  }

  /**
   * Adjust wave size based on performance
   */
  private adjustWaveSize(
    currentSize: number,
    successCount: number,
    totalCount: number,
    durationMs: number
  ): number {
    const successRate = successCount / totalCount;
    const avgTime = durationMs / totalCount;

    // Increase size if performing well
    if (successRate > 0.9 && avgTime < 3000) {
      return Math.min(
        Math.floor(currentSize * this.dynamicWaveSize.adjustmentFactor),
        this.dynamicWaveSize.maxSize
      );
    }

    // Decrease size if struggling
    if (successRate < 0.5 || avgTime > 10000) {
      return Math.max(
        Math.floor(currentSize / this.dynamicWaveSize.adjustmentFactor),
        this.dynamicWaveSize.minSize
      );
    }

    return currentSize;
  }

  /**
   * Update performance metrics
   */
  private updatePerformanceMetrics(agents: AgentInstance[]): void {
    const successful = agents.filter(a => a.status === 'completed');
    const failed = agents.filter(a => a.status === 'failed');

    if (successful.length > 0) {
      const avgSuccess = successful.reduce((sum, a) => sum + (a.duration_ms || 0), 0) / successful.length;
      this.performanceMetrics.avgSuccessTime = 
        (this.performanceMetrics.avgSuccessTime + avgSuccess) / 2;
    }

    if (failed.length > 0) {
      const avgFailure = failed.reduce((sum, a) => sum + (a.duration_ms || 0), 0) / failed.length;
      this.performanceMetrics.avgFailureTime = 
        (this.performanceMetrics.avgFailureTime + avgFailure) / 2;
    }

    this.performanceMetrics.totalExecutions += agents.length;
  }

  /**
   * Execute single agent with caching, circuit breaker, and adaptive retry
   */
  private async executeAgent(
    agent: AgentInstance,
    onProgress?: (agent: AgentInstance) => void
  ): Promise<AgentInstance> {
    agent.status = 'running';
    onProgress?.(agent);
    
    const startTime = Date.now();

    try {
      // Step 1: Check cache first
      const cacheKey = AgentCacheManager.generateKey(
        agent.task_id,
        agent.target,
        agent.graph_api_call || agent.web_api_call
      );

      const cached = await agentCacheManager.get(cacheKey);
      if (cached) {
        agent.result = cached;
        agent.status = 'completed';
        agent.duration_ms = Date.now() - startTime;
        console.log(`   ⚡ ${agent.agent_id} served from cache`);
        onProgress?.(agent);
        return agent;
      }

      // Step 2: Execute with circuit breaker and retry protection
      const result = await circuitBreakerManager.executeProtected(
        agent.agent_id,
        async () => {
          return await retryStrategyManager.executeWithRetry(
            agent.agent_id,
            async () => {
              // Execute based on API type
              if (agent.graph_api_call) {
                return await this.executeGraphApiCall(agent);
              } else if (agent.web_api_call) {
                return await this.executeWebApiCall(agent);
              }
              throw new Error('No API call configuration found');
            },
            {
              maxRetries: 3,
              initialDelayMs: 1000,
              maxDelayMs: 30000,
              backoffMultiplier: 2,
              timeoutMs: 30000,
              jitterFactor: 0.2
            }
          );
        },
        {
          failureThreshold: 5,
          successThreshold: 2,
          timeout: 60000,
          monitoringPeriod: 300000
        }
      );

      // Extract result from retry wrapper
      if (result && 'success' in result && result.success) {
        agent.result = result.result;
      } else if (result && 'success' in result && !result.success) {
        throw result.error || new Error('Execution failed');
      } else {
        agent.result = result;
      }

      // Step 3: Cache successful result
      await agentCacheManager.set(
        cacheKey,
        agent.result,
        300000, // 5 minutes TTL
        [agent.task_id.split('_')[0], 'agent_result']
      );

      agent.status = 'completed';
      agent.duration_ms = Date.now() - startTime;
      onProgress?.(agent);
      return agent;

    } catch (error) {
      agent.status = 'failed';
      agent.error = error instanceof Error ? error.message : 'Unknown error';
      agent.duration_ms = Date.now() - startTime;
      console.error(`   ❌ ${agent.agent_id} failed: ${agent.error}`);
      onProgress?.(agent);
      return agent;
    }
  }

  /**
   * Execute Graph API call through gpt5NanoAgent
   */
  private async executeGraphApiCall(agent: AgentInstance): Promise<any> {
    // Build user intent string that triggers the right action
    const userIntent = this.buildUserIntentFromAgent(agent);
    
    // Use gpt5NanoAgent to process and execute
    const response = await gpt5NanoAgent.processUserIntent(userIntent);
    
    if (!response.validation.isValid) {
      throw new Error(`Validation failed: ${response.validation.errors.join(', ')}`);
    }

    const result = await gpt5NanoAgent.executeAction(response.action);
    return result;
  }

  /**
   * Execute web API call
   */
  private async executeWebApiCall(agent: AgentInstance): Promise<any> {
    const searchResults = await blink.data.search(agent.web_api_call!.query, {
      limit: 10
    });

    // Use AI to structure the results
    const { text } = await blink.ai.generateText({
      prompt: `${agent.prompt}\n\nSearch Results:\n${JSON.stringify(searchResults.organic_results?.slice(0, 5) || [], null, 2)}\n\nProvide structured analysis in JSON format matching this schema:\n${JSON.stringify(agent.structured_output_schema, null, 2)}`,
      maxTokens: 800
    });

    const jsonMatch = text.match(/\{[\s\S]*\}/);
    return jsonMatch ? safeJsonParse(jsonMatch[0]) : { raw: text };
  }

  /**
   * Build user intent string from agent configuration
   */
  private buildUserIntentFromAgent(agent: AgentInstance): string {
    const taskType = agent.task_id;
    const target = agent.target;

    if (taskType.includes('email_search')) {
      return `Search emails from ${target}`;
    } else if (taskType.includes('calendar_search_by_user')) {
      return `Find meetings with ${target}`;
    } else if (taskType.includes('calendar_search_by_topic')) {
      return `Search meetings about ${target}`;
    } else if (taskType.includes('topic_search')) {
      return `Search emails about ${target}`;
    }

    return `Process ${target}`;
  }

  /**
   * Aggregate results and generate insights
   */
  private async aggregateResults(
    plan: OrchestrationPlan,
    agents: AgentInstance[],
    totalDuration: number
  ): Promise<OrchestrationResult> {
    console.log('\n📊 Aggregating Results and Generating Insights');
    
    const successfulAgents = agents.filter(a => a.status === 'completed');
    const successRate = successfulAgents.length / agents.length;

    // Group results by category
    const resultsByCategory: Record<string, any> = {};
    
    agents.forEach(agent => {
      const category = agent.task_id.split('_')[0]; // 'email', 'calendar', 'topic', etc.
      if (!resultsByCategory[category]) {
        resultsByCategory[category] = {
          agents_deployed: 0,
          results: []
        };
      }
      resultsByCategory[category].agents_deployed++;
      if (agent.result) {
        resultsByCategory[category].results.push(agent.result);
      }
    });

    // Generate cross-correlation insights using AI
    const { text: insightsText } = await blink.ai.generateText({
      prompt: `Analyze these multi-agent results and identify cross-correlation insights and patterns:\n\n${JSON.stringify(resultsByCategory, null, 2)}\n\nProvide 3-5 key insights that connect findings across different categories. Format as JSON array of strings.`,
      maxTokens: 500
    });

    const insightsMatch = insightsText.match(/\[[\s\S]*\]/);
    const insights = insightsMatch ? safeJsonParse(insightsMatch[0]) : [];

    // Generate recommended actions
    const { text: actionsText } = await blink.ai.generateText({
      prompt: `Based on these results and insights, suggest 3-5 concrete recommended actions:\n\nResults: ${JSON.stringify(resultsByCategory, null, 2)}\nInsights: ${JSON.stringify(insights, null, 2)}\n\nProvide as JSON array of actionable strings.`,
      maxTokens: 500
    });

    const actionsMatch = actionsText.match(/\[[\s\S]*\]/);
    const actions = actionsMatch ? safeJsonParse(actionsMatch[0]) : [];

    return {
      success: successRate > 0.5,
      workflow_summary: {
        total_tasks: plan.task_breakdown.length,
        total_agents_deployed: agents.length,
        execution_time_ms: totalDuration,
        success_rate: successRate
      },
      results_by_category: resultsByCategory,
      cross_correlation_insights: insights,
      recommended_actions: actions,
      raw_agent_results: agents
    };
  }
}

/**
 * Multi-Agent Orchestrator Main Class
 */
export class MultiAgentOrchestrator {
  private parser = new IntentParser();
  private provisioner = new AgentProvisioner();
  private engine = new ExecutionEngine();

  /**
   * Main orchestration flow
   */
  async orchestrate(
    userRequest: string,
    onProgress?: (update: {
      phase: 'parsing' | 'provisioning' | 'executing' | 'aggregating';
      data: any;
    }) => void
  ): Promise<OrchestrationResult> {
    console.log('\n🚀 Multi-Agent Orchestration Started');
    console.log(`📝 User Request: "${userRequest}"\n`);

    try {
      // Phase 1: Parse intent
      onProgress?.({ phase: 'parsing', data: { message: 'Parsing intent with count recognition...' } });
      const parsedIntent = await this.parser.parseIntent(userRequest);
      console.log('✅ Phase 1 Complete - Parsed Intent:', JSON.stringify(parsedIntent, null, 2));

      // Phase 2: Provision agents
      onProgress?.({ phase: 'provisioning', data: { message: 'Provisioning specialized agents...' } });
      const plan = this.provisioner.provisionAgents(parsedIntent);
      console.log(`✅ Phase 2 Complete - Provisioned ${plan.total_agents_needed} agents in ${plan.execution_waves.length} waves`);
      onProgress?.({ phase: 'provisioning', data: { plan } });

      // Phase 3 & 4: Execute and aggregate
      onProgress?.({ phase: 'executing', data: { message: 'Executing agents in parallel waves...' } });
      const result = await this.engine.execute(plan, (agent) => {
        onProgress?.({ 
          phase: 'executing', 
          data: { 
            agent_id: agent.agent_id, 
            status: agent.status,
            target: agent.target
          } 
        });
      });

      onProgress?.({ phase: 'aggregating', data: { result } });
      console.log('\n✅ Orchestration Complete!');
      console.log(`   Success Rate: ${(result.workflow_summary.success_rate * 100).toFixed(1)}%`);
      console.log(`   Total Duration: ${result.workflow_summary.execution_time_ms}ms`);

      return result;
    } catch (error) {
      console.error('❌ Orchestration failed:', error);
      throw error;
    }
  }
}

/**
 * Singleton instance
 */
export const multiAgentOrchestrator = new MultiAgentOrchestrator();

/**
 * Example usage demonstrating count-based multi-agent orchestration
 */
export async function exampleUsage() {
  const result = await multiAgentOrchestrator.orchestrate(
    'Find meetings by 2 users (alice@company.com, bob@company.com) and search 3 topics (project_alpha, budget_review, team_updates)',
    (update) => {
      console.log(`[${update.phase.toUpperCase()}]`, update.data);
    }
  );

  console.log('\n📊 Final Results:');
  console.log(JSON.stringify(result, null, 2));
}
